import React, { FC, memo, useEffect, useMemo } from "react";
import { useDialogShadow } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { ImportWizard } from "./ImportWizard";
import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { ImportTargetRadioGroup } from "../styled";
import { ResourceMeta } from "../../DialogComponents/ResourceMeta/ResourceMeta";
import { useContext } from "../hooks/useContext";
import { useImportContext } from "../importContext";
import { ImportTargetType } from "../types";
import { ResourceMeta as ResourceMetaType } from "../../../../hooks/useResourceMeta";

const ImportTargetComponent: FC = () => {
  const { t } = useTranslation("dialogs");
  const context = useContext();
  const { nextStep, prevStep, meta, updateMeta, target, setTarget } = useImportContext();
  const { shadows, setScrollContainer } = useDialogShadow();

  const options = useMemo(
    () => [
      { value: ImportTargetType.Catalog.toString(), label: t("import.importToCatalog") },
      { value: ImportTargetType.NewMap.toString(), label: t("import.importToMap") },
    ],
    [t],
  );

  const getMeta: ResourceMetaType = useMemo(
    () => ({
      ...meta,
      aliasLabel: t("import.projectName"),
      descriptionLabel: t("import.projectDescription"),
    }),
    [meta, t],
  );

  useEffect(() => {
    const { setAlias, setDescription } = updateMeta;

    setAlias(t("import.importProjectAliasGDB"));
    setDescription(t("import.importProjectDescription"));
  }, []);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} extra={<ImportWizard />} />
      <DialogContent ref={setScrollContainer} style={{ paddingTop: "0" }}>
        <RestrictedWidthContainer>
          <ImportTargetRadioGroup
            options={options}
            value={target.toString()}
            column
            onChange={e => setTarget(Number(e.target.value))}
          />
          {target === ImportTargetType.NewMap && (
            <ProjectMetaBlock>
              <ResourceMeta meta={getMeta} updateMeta={updateMeta} />
            </ProjectMetaBlock>
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyText={t("actions.next", { ns: "common" })}
        backButtonText={t("import.back")}
        onBackButton={prevStep}
        onApply={nextStep}
      />
    </FullScreenDialog>
  );
};

export const ImportTarget: FC = memo(ImportTargetComponent);
